import { useState, useEffect, Dispatch, SetStateAction } from "react";
import {
  Input,
  Button,
  FormField,
  Select,
  SpaceBetween,
  RadioGroup,
  Form,
  Container,
  ColumnLayout,
  Box,
  Grid,
  Checkbox,
} from "@cloudscape-design/components-themed";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { isLengthValid, isHalfNumberValid } from "../../utils/inputValidation";
import { useGetComprehensiveCompaniesQuery } from "../../features/api";
import { getMessage } from "../../features/error/messages";
import {
  INITIAL_SEARCH_CRITERIA,
  setSearchCriteria,
} from "../../features/slices/individual-handling-list";
import { formatDate } from "../../utils/format";
import {
  addBlank,
  CODE_MASTER,
  COMMON_LABELS,
  DivisionDict,
  getDivisionLabel,
} from "../default-constants";
import { LABELS, PLACE_APPLICATION } from "./constants";
import { useCodelistHook } from "../../app/useCodelistHook";
import { getComprehensiveMerchantItems } from "../../utils/common";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import PeriodDatePicker from "../UI/date-picker/PeriodDatePicker";
import { concat, isEmpty, without } from "lodash";
import ApplicationFormTypeModal from "./ApplicationFormTypeModal";

interface ISearchBar {
  setSkip: Dispatch<SetStateAction<boolean>>;
}

/**
 * 個別取扱確認結果一覧 検索エリア
 */
const SearchBar = (props: ISearchBar) => {
  const { setSkip } = props;
  const dispatch = useAppDispatch();
  const { searchCriteria } = useAppSelector(
    (state) => state.individualHandlingList
  );
  const { individualHandlingReason, applicationFormType } = useCodelistHook();
  const [searchInput, setSearchInput] = useState(INITIAL_SEARCH_CRITERIA);
  const [applicationNoErrorMsg, setApplicationNoErrorMsg] = useState("");
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");
  const [merchantsNameErrorMsg, setMerchantsNameErrorMsg] = useState("");
  const [individualHandlingReasonItems, setIndividualHandlingReasonItems] =
    useState<DivisionDict[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { data: comprehensiveMerchantListItem } =
    useGetComprehensiveCompaniesQuery({
      companyCode: "",
      invisibleComprehensiveCompanyNoList: [],
    });
  const comprehensiveMerchantListItems = addBlank(
    getComprehensiveMerchantItems(comprehensiveMerchantListItem)
  );
  const CONFIRMATION_STATUS = addBlank(CODE_MASTER["CONFIRMATION_STATUS"]);

  useEffect(() => {
    setSearchInput(searchCriteria);
  }, [searchCriteria]);

  useEffect(() => {
    if (!isEmpty(individualHandlingReason)) {
      setIndividualHandlingReasonItems(addBlank(individualHandlingReason));
    }
  }, [individualHandlingReason]);

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setSkip(true);
    setSearchInput(INITIAL_SEARCH_CRITERIA);
    setApplicationNoErrorMsg("");
    setCompanyNameErrorMsg("");
    setMerchantsNameErrorMsg("");
  };

  /**
   * 検索ボタンを押したときの処理.
   * @param {CustomEvent<ClickDetail>} e
   */
  const handleOnSubmit = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    if (
      checkApplicationNo(searchInput.applicationNo) &&
      checkCompanyName(searchInput.companyName) &&
      checkMerchantsName(searchInput.merchantName)
    ) {
      setSkip(false);
      dispatch(
        setSearchCriteria({ ...searchInput, timestamp: new Date().getTime() })
      );
    }
  };

  /**
   * 事業者名のバリデーションチェック
   * @param {string} companyName チェックする事業者名
   * @returns {boolean} true: エラーなし
   */
  const checkCompanyName = (companyName: string): boolean => {
    if (!isLengthValid(companyName, 30)) {
      setCompanyNameErrorMsg(getMessage("KGRERR058", 30).label);
      return false;
    } else {
      setCompanyNameErrorMsg("");
      return true;
    }
  };

  /**
   * 加盟店名バリデーションチェック
   * @param {string} merchantsName チェックする加盟店名
   * @returns  {boolean} true: エラーなし
   */
  const checkMerchantsName = (merchantsName: string): boolean => {
    if (!isLengthValid(merchantsName, 15)) {
      setMerchantsNameErrorMsg(getMessage("KGRERR058", 15).label);
      return false;
    } else {
      setMerchantsNameErrorMsg("");
      return true;
    }
  };

  /**
   * 申請番号バリデーションチェック
   * @param {string} applicationNo チェックする 申請番号
   * @returns  {boolean} true: エラーなし
   */
  const checkApplicationNo = (applicationNo: string): boolean => {
    if (!isHalfNumberValid(applicationNo)) {
      setApplicationNoErrorMsg(getMessage("KGRERR006").label);
      return false;
    } else {
      setApplicationNoErrorMsg("");
      return true;
    }
  };

  /**
   * チェックボックスの汎用的な入力ハンドラ.
   * 更新対象のプロパティのデータ型はstringの配列でなければならない.
   * @param key 更新対象のプロパティ名
   * @param value 値
   */
  const checkBoxOnChange = <K extends keyof typeof searchInput>(
    key: K,
    value: string
  ) => {
    const currentValues = searchInput[key] as string[];
    setSearchInput({
      ...searchInput,
      [key]: currentValues.includes(value)
        ? without(currentValues, value)
        : concat(currentValues, value),
    });
  };

  return (
    <Form
      actions={
        <Button variant="normal" onClick={handleOnSubmit}>
          {COMMON_LABELS.BTNS.SEARCH}
        </Button>
      }
    >
      <Container
        header={
          <ColumnLayout>
            <Box float="right">
              <Button variant="normal" onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          <SpaceBetween direction="vertical" size="l">
            <Grid
              gridDefinition={[{ colspan: 3 }, { colspan: 4 }, { colspan: 3 }]}
            >
              <FormField
                label={LABELS.APPLICATION_NO}
                errorText={applicationNoErrorMsg}
                constraintText="半角数字"
              >
                <Input
                  name="applicationNo"
                  value={searchInput.applicationNo}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({ ...searchInput, applicationNo: value });
                    checkApplicationNo(value);
                  }}
                />
              </FormField>
              <PeriodDatePicker
                label={LABELS.APPLICATION_DATE}
                startDate={searchInput.startDate}
                startDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    startDate: formatDate(value),
                  })
                }
                endDate={searchInput.endDate}
                endDateOnChange={({ detail: { value } }) =>
                  setSearchInput({
                    ...searchInput,
                    endDate: formatDate(value),
                  })
                }
              />
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                <FormField label={LABELS.CONTRACT_STATUS}>
                  <RadioGroup
                    items={[
                      {
                        value: "",
                        label: LABELS.ALL,
                      },
                      {
                        value: "直接",
                        label: LABELS.CONTRACT_DIRECTLY,
                      },
                      {
                        value: "包括",
                        label: LABELS.CONTRACT_COMPREHENSIVE,
                      },
                    ]}
                    onChange={({ detail: { value } }) =>
                      setSearchInput({
                        ...searchInput,
                        contractType: value,
                        contractRoute: "",
                      })
                    }
                    value={searchInput.contractType}
                  />
                </FormField>
                <FormField label={LABELS.CONTRACT_ROUTE}>
                  <Select
                    disabled={
                      searchInput.contractType !== LABELS.CONTRACT_COMPREHENSIVE
                    }
                    selectedOption={{
                      value: searchInput.contractRoute,
                      label: getDivisionLabel(
                        comprehensiveMerchantListItems,
                        searchInput.contractRoute
                      ),
                    }}
                    options={comprehensiveMerchantListItems}
                    placeholder="Choose a group"
                    filteringType="auto"
                    onChange={({ detail: { selectedOption } }) =>
                      setSearchInput({
                        ...searchInput,
                        contractRoute: selectedOption.value!,
                      })
                    }
                  />
                </FormField>
              </Grid>
            </Grid>
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.COMPANY_NAME}
                errorText={companyNameErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE}
              >
                <Input
                  name="companyName"
                  value={searchInput.companyName}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({
                      ...searchInput,
                      companyName: value,
                    });
                    checkCompanyName(value);
                  }}
                />
              </FormField>
              <FormField
                label={LABELS.MERCHANTS_NAME}
                errorText={merchantsNameErrorMsg}
                constraintText={PLACE_APPLICATION.FONT_SIZE_MERCHANTS}
              >
                <Input
                  name="merchantsName"
                  value={searchInput.merchantName}
                  onChange={({ detail: { value } }) => {
                    setSearchInput({
                      ...searchInput,
                      merchantName: value,
                    });
                    checkMerchantsName(value);
                  }}
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField label={LABELS.INDIVIDUAL_HANDLING_REASON}>
                <Select
                  selectedOption={{
                    value: searchInput.individualHandlingReason,
                    label: getDivisionLabel(
                      individualHandlingReasonItems,
                      searchInput.individualHandlingReason
                    ),
                  }}
                  options={individualHandlingReasonItems}
                  placeholder="Choose a group"
                  filteringType="auto"
                  onChange={({ detail: { selectedOption } }) =>
                    setSearchInput({
                      ...searchInput,
                      individualHandlingReason: selectedOption.value!,
                    })
                  }
                />
              </FormField>
              <FormField label={LABELS.STATUS}>
                <Select
                  selectedOption={{
                    value: searchInput.status,
                    label: getDivisionLabel(
                      CONFIRMATION_STATUS,
                      searchInput.status
                    ),
                  }}
                  options={CONFIRMATION_STATUS}
                  placeholder="Choose a group"
                  filteringType="auto"
                  onChange={({ detail: { selectedOption } }) =>
                    setSearchInput({
                      ...searchInput,
                      status: selectedOption.value!,
                    })
                  }
                />
              </FormField>
            </ColumnLayout>
            <FormField
              label={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  {LABELS.APPLICATION_FORM_TYPE}
                  <Button
                    iconName="status-info"
                    variant="link"
                    onClick={() => setIsOpen(true)}
                  />
                </div>
              }
            >
              <SpaceBetween direction="horizontal" size="xl">
                {applicationFormType.map((item) => (
                  <Checkbox
                    key={item.label}
                    onChange={() =>
                      checkBoxOnChange("applicationFormTypeCodes", item.value)
                    }
                    checked={searchInput.applicationFormTypeCodes.includes(
                      item.value
                    )}
                  >
                    {item.label}
                  </Checkbox>
                ))}
              </SpaceBetween>
              {isOpen && (
                <ApplicationFormTypeModal
                  visible={isOpen}
                  onClose={() => setIsOpen(false)}
                  items={applicationFormType}
                />
              )}
            </FormField>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default SearchBar;
